import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Calendar, DollarSign, CheckSquare, Users, Camera, Plus, AlertCircle, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { getTrips, getActiveTrip } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Dashboard Organisateur - Vue Chef de Voyage
 * - Voyage actif mis en avant
 * - Stats rapides (budget, tâches, itinéraire)
 * - Quick actions (FAB)
 * - Alertes urgentes
 */
export default function OrganizerDashboard() {
  const navigate = useNavigate();
  const { families } = useAuth();
  const [activeTrip, setActiveTrip] = useState(null);
  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const familyId = families?.[0]?.family_id;
      const [tripsRes, activeTripRes] = await Promise.all([
        familyId ? getTrips(familyId) : Promise.resolve({ data: [] }),
        getActiveTrip().catch(() => null)
      ]);

      setAllTrips(tripsRes.data || []);
      setActiveTrip(activeTripRes?.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="h-48 bg-[#E2E8F0] rounded-2xl animate-pulse" />;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Active Trip Card */}
      {activeTrip && (
        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-[#1E293B]">{activeTrip.name}</h2>
                <p className="text-sm text-[#64748B]">
                  {activeTrip.destination} • {getRemainingDays(activeTrip.end_date)} jours restants
                </p>
              </div>
              <button
                onClick={() => navigate(`/trips/${activeTrip.trip_id}`)}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-xl font-medium hover:opacity-90"
              >
                Voir
              </button>
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-white rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                style={{ width: `${getTripProgress(activeTrip)}%` }}
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                icon={MapPin}
                label="Aujourd'hui"
                value={`Jour ${getCurrentDay(activeTrip)}`}
                color="text-purple-600"
              />
              <StatCard
                icon={DollarSign}
                label="Budget"
                value={`${activeTrip.budget_spent || 0}€`}
                sublabel={`/ ${activeTrip.budget_total || 0}€`}
                color="text-green-600"
              />
              <StatCard
                icon={CheckSquare}
                label="Tâches"
                value={`${activeTrip.tasks_completed || 0}/${activeTrip.tasks_total || 0}`}
                color="text-blue-600"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts */}
      {hasAlerts(activeTrip) && (
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-[#1E293B] mb-2">Alertes (2)</h3>
                <ul className="space-y-1 text-sm text-[#64748B]">
                  <li>• Passeport Marie expire dans 10 jours</li>
                  <li>• Réservation resto ce soir 19h00</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickAction
          icon={Camera}
          label="Scanner Reçu"
          gradient="from-purple-500 to-pink-500"
          onClick={() => navigate('/scan-receipt')}
        />
        <QuickAction
          icon={Plus}
          label="Activité"
          gradient="from-blue-500 to-cyan-500"
          onClick={() => navigate('/add-activity')}
        />
        <QuickAction
          icon={DollarSign}
          label="Budget"
          gradient="from-green-500 to-emerald-500"
          onClick={() => navigate(`/trips/${activeTrip?.trip_id}/budget`)}
        />
        <QuickAction
          icon={Users}
          label="Famille"
          gradient="from-orange-500 to-red-500"
          onClick={() => navigate('/family')}
        />
      </div>

      {/* All Trips */}
      <div>
        <h2 className="text-lg font-semibold text-[#1E293B] mb-3">Tous mes voyages</h2>
        <div className="grid gap-3">
          {allTrips.map(trip => (
            <TripCard key={trip.trip_id} trip={trip} onClick={() => navigate(`/trips/${trip.trip_id}`)} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper Components
function StatCard({ icon: Icon, label, value, sublabel, color }) {
  return (
    <div className="bg-white p-3 rounded-xl">
      <Icon className={`w-4 h-4 ${color} mb-1`} />
      <p className="text-xs text-[#64748B]">{label}</p>
      <p className="text-lg font-bold text-[#1E293B]">
        {value}
        {sublabel && <span className="text-xs text-[#64748B] font-normal"> {sublabel}</span>}
      </p>
    </div>
  );
}

function QuickAction({ icon: Icon, label, gradient, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br ${gradient} text-white hover:opacity-90 transition-opacity`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function TripCard({ trip, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 bg-white rounded-xl border border-[#E2E8F0] hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#1E293B]">{trip.name}</h3>
          <p className="text-sm text-[#64748B]">{trip.destination}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[#64748B]">{formatDate(trip.start_date)}</p>
          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(trip.status)}`}>
            {getStatusLabel(trip.status)}
          </span>
        </div>
      </div>
    </button>
  );
}

// Helper Functions
function getRemainingDays(endDate) {
  const end = new Date(endDate);
  const now = new Date();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function getTripProgress(trip) {
  const start = new Date(trip.start_date);
  const end = new Date(trip.end_date);
  const now = new Date();
  const total = end - start;
  const elapsed = now - start;
  return Math.min(Math.max((elapsed / total) * 100, 0), 100);
}

function getCurrentDay(trip) {
  const start = new Date(trip.start_date);
  const now = new Date();
  const diff = Math.ceil((now - start) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 1;
}

function hasAlerts(trip) {
  return trip && (trip.alerts_count > 0 || true); // Mock for now
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function getStatusColor(status) {
  const colors = {
    upcoming: 'bg-blue-100 text-blue-700',
    active: 'bg-green-100 text-green-700',
    completed: 'bg-gray-100 text-gray-700'
  };
  return colors[status] || colors.upcoming;
}

function getStatusLabel(status) {
  const labels = {
    upcoming: 'À venir',
    active: 'En cours',
    completed: 'Terminé'
  };
  return labels[status] || status;
}
